import { useEffect, useState } from "react";
import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  Timestamp,
  deleteDoc,
  doc,
} from "firebase/firestore";
import { db } from "../firebase";

export default function FundTransfer() {
  const [members, setMembers] = useState([]);

  const [fromSearch, setFromSearch] = useState("");
  const [toSearch, setToSearch] = useState("");

  const [fromMember, setFromMember] = useState(null);
  const [toMember, setToMember] = useState(null);

  const [fromBalance, setFromBalance] = useState(0);
  const [toBalance, setToBalance] = useState(0);

  const [amount, setAmount] = useState("");
  const [transfers, setTransfers] = useState([]);

  // 🔹 LOAD MEMBERS
  useEffect(() => {
    const loadMembers = async () => {
      const snap = await getDocs(collection(db, "members"));
      setMembers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    };
    loadMembers();
  }, []);

  // 🔹 LOAD TRANSFERS
  const loadTransfers = async () => {
    const snap = await getDocs(collection(db, "fundTransfers"));
    setTransfers(
      snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => b.createdAt?.seconds - a.createdAt?.seconds)
    );
  };

  useEffect(() => {
    loadTransfers();
  }, []);

  // 🔹 CALCULATE BALANCE (SAFE)
  const calculateBalance = async (memberId) => {
    if (!memberId) return 0;

    const q = query(
      collection(db, "savingLedger"),
      where("memberId", "==", memberId)
    );

    const snap = await getDocs(q);

    let balance = 0;
    snap.forEach(d => {
      const row = d.data();
      balance += Number(row.deposit || 0);
      balance -= Number(row.withdrawal || 0);
    });

    return balance;
  };

  // 🔹 SEARCH MEMBER (SAFE)
  const searchMember = async (text, setMember, setBalance) => {
    if (!text) {
      setMember(null);
      setBalance(0);
      return;
    }

    const found = members.find(
      m =>
        m.memberId === text ||
        m.mobile === text ||
        m.name?.toLowerCase().includes(text.toLowerCase())
    );

    if (!found || !found.memberId) {
      setMember(null);
      setBalance(0);
      return;
    }

    setMember(found);
    const bal = await calculateBalance(found.memberId);
    setBalance(bal);
  };

  // 🔥 TRANSFER
  const handleTransfer = async () => {
    if (!fromMember || !toMember || !amount) {
      alert("All fields required");
      return;
    }

    const amt = Number(amount);
    if (amt <= 0) {
      alert("Invalid amount");
      return;
    }

    if (fromBalance < amt) {
      alert("Insufficient balance");
      return;
    }

    const now = Timestamp.now();

    // FROM → WITHDRAW
    await addDoc(collection(db, "savingLedger"), {
      memberId: fromMember.memberId,
      withdrawal: amt,
      deposit: 0,
      particular: `Fund transfer to ${toMember.memberId}`,
      createdAt: now,
      entryBy: "Admin",
    });

    // TO → DEPOSIT
    await addDoc(collection(db, "savingLedger"), {
      memberId: toMember.memberId,
      deposit: amt,
      withdrawal: 0,
      particular: `Fund received from ${fromMember.memberId}`,
      createdAt: now,
      entryBy: "Admin",
    });

    // TRANSFER RECORD
    await addDoc(collection(db, "fundTransfers"), {
      fromMemberId: fromMember.memberId,
      fromName: fromMember.name,
      fromMobile: fromMember.mobile,

      toMemberId: toMember.memberId,
      toName: toMember.name,
      toMobile: toMember.mobile,

      amount: amt,
      status: "SUCCESS",
      createdAt: now,
    });

    alert("Transfer Successful");

    setAmount("");
    setFromSearch("");
    setToSearch("");
    setFromMember(null);
    setToMember(null);
    setFromBalance(0);
    setToBalance(0);

    loadTransfers();
  };

  // 🔥 DELETE + ROLLBACK
  const handleDelete = async (t) => {
    if (!window.confirm("Rollback this transfer?")) return;

    const now = Timestamp.now();

    // REVERSE → CREDIT BACK TO FROM MEMBER
    await addDoc(collection(db, "savingLedger"), {
      memberId: t.fromMemberId,
      deposit: t.amount,
      withdrawal: 0,
      particular: `Rollback from ${t.toMemberId}`,
      createdAt: now,
      entryBy: "Admin",
    });

    // REVERSE → WITHDRAW FROM TO MEMBER
    await addDoc(collection(db, "savingLedger"), {
      memberId: t.toMemberId,
      withdrawal: t.amount,
      deposit: 0,
      particular: `Rollback to ${t.fromMemberId}`,
      createdAt: now,
      entryBy: "Admin",
    });

    await deleteDoc(doc(db, "fundTransfers", t.id));

    alert("Transfer Rolled Back");
    loadTransfers();
  };

  return (
    <div>
      <h3>Admin Fund Transfer</h3>

      {/* FROM MEMBER */}
      <h5>From Member</h5>
      <input
        placeholder="Search by ID / Name / Mobile"
        value={fromSearch}
        onChange={(e) => {
          setFromSearch(e.target.value);
          searchMember(e.target.value, setFromMember, setFromBalance);
        }}
      />
      {fromMember && (
        <p>
          {fromMember.name} | {fromMember.memberId} | {fromMember.mobile} |
          Balance: ₹{fromBalance}
        </p>
      )}

      {/* TO MEMBER */}
      <h5>To Member</h5>
      <input
        placeholder="Search by ID / Name / Mobile"
        value={toSearch}
        onChange={(e) => {
          setToSearch(e.target.value);
          searchMember(e.target.value, setToMember, setToBalance);
        }}
      />
      {toMember && (
        <p>
          {toMember.name} | {toMember.memberId} | {toMember.mobile} |
          Balance: ₹{toBalance}
        </p>
      )}

      <input
        type="number"
        placeholder="Amount"
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
      />

      <br /><br />
      <button onClick={handleTransfer}>Transfer</button>

      <hr />
      <h4>Transfer Report</h4>

      <table border="1" cellPadding="6">
        <thead>
          <tr>
            <th>#</th>
            <th>From</th>
            <th>To</th>
            <th>Amount</th>
            <th>Date</th>
            <th>Status</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {transfers.map((t, i) => (
            <tr key={t.id}>
              <td>{i + 1}</td>
              <td>{t.fromName} ({t.fromMemberId})</td>
              <td>{t.toName} ({t.toMemberId})</td>
              <td>₹{t.amount}</td>
              <td>{t.createdAt?.toDate().toLocaleString()}</td>
              <td>{t.status}</td>
              <td>
                <button onClick={() => handleDelete(t)}>Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}